import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Row,
  Spinner,
} from 'react-bootstrap';

import {
  fetchInstallerInfo,
  getInstallerExists,
  getInstallerIsLoading,
  getMSDataByEntity,
  installMS,
  removeMS,
  updateMSVersion,
  startInstallerLoader,
  startMS,
  stopInstallerLoader,
  stopMS,
} from 'rdx/hardware/installer';

function ButtonWithMSVersions({ microService, action, actionName }) {
  const { latestVersion, installedVersion, builds } = useSelector(
    getMSDataByEntity(microService),
  );
  const [selectedVersion, setVersion] = useState(latestVersion);
  const selectVersion = (v) => {
    setVersion(v);
  };

  return (
    <Dropdown as={ButtonGroup} onSelect={selectVersion} className="w-100">
      <Button
        variant="info"
        className="w-100"
        onClick={() => action(selectedVersion)}
      >{`${actionName} ${
        selectedVersion === latestVersion ? 'latest' : selectedVersion
      }`}</Button>

      <Dropdown.Toggle split variant="info" id="dropdown-ms-version" />

      <Dropdown.Menu onSelect={selectVersion}>
        {builds.map((v) => {
          const alreadySelected = v === selectedVersion;
          const alreadyInstalled = v === installedVersion;
          return (
            <Dropdown.Item
              key={v}
              eventKey={v}
              disabled={alreadyInstalled}
              active={alreadySelected}
              style={{ opacity: alreadyInstalled ? 0.6 : 1 }}
            >
              {v}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function MicroserviceInstallation({ microService }) {
  const dispatch = useDispatch();

  const installerExist = useSelector(getInstallerExists);
  const isLoading = useSelector(getInstallerIsLoading);
  const ms = useSelector(getMSDataByEntity(microService));
  const { entity, msIsInstalled, msIsAvailable, installedVersion, status } = ms;

  const serviceIsRunning = status === 'StatusRunning';

  const startMicroService = (version) => dispatch(startMS({ entity, version }));
  const stopMicroService = (version) => dispatch(stopMS({ entity, version }));

  const installMicroService = (version) =>
    dispatch(installMS({ entity, version }));

  const removeMicroService = (version) =>
    dispatch(removeMS({ entity, version }));

  const updateMSToVersionX = (version) =>
    dispatch(updateMSVersion({ entity, version }));

  const updateInstallerInfo = async () => {
    dispatch(startInstallerLoader());
    await dispatch(fetchInstallerInfo());
    dispatch(stopInstallerLoader());
  };

  if (isLoading)
    return (
      <Card className="text-center mt-3">
        <Card.Body>
          <Spinner animation="grow" />
        </Card.Body>
      </Card>
    );

  if (!installerExist) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">
            Installer is not Installed. Go to{' '}
            <Link to="/hardware/installer/installation">
              Installation section
            </Link>{' '}
            to see how to download and start it.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!msIsAvailable) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="warning">Such microservice does not exist.</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <Row
          noGutters={true}
          className="d-flex w-100 px-1 justify-content-between align-items-baseline"
        >
          <Col xs={12} md={12} lg={4}>
            <Alert
              className="info w-100 text-center"
              variant={
                // eslint-disable-next-line no-nested-ternary
                msIsInstalled
                  ? serviceIsRunning
                    ? 'success'
                    : 'warning'
                  : 'danger'
              }
            >{`${
              msIsInstalled
                ? `Installed ${
                    serviceIsRunning ? 'and started' : ' but stopped'
                  }`
                : `Not installed`
            }`}</Alert>
          </Col>
          {msIsInstalled ? (
            <>
              <Col xs={12} md={3} lg={2} className="h4 ">
                <Button
                  disabled={isLoading}
                  variant={serviceIsRunning ? `warning` : `success`}
                  className="w-100"
                  onClick={() =>
                    serviceIsRunning
                      ? stopMicroService(installedVersion)
                      : startMicroService(installedVersion)
                  }
                >
                  <span className="text-white">
                    {serviceIsRunning ? 'Stop' : 'Start'}
                  </span>
                </Button>
              </Col>
              <Col xs={12} md={3} lg={2} className="h4 ">
                <Button
                  disabled={isLoading}
                  variant="danger"
                  className="w-100"
                  onClick={() => removeMicroService(installedVersion)}
                >
                  Remove
                </Button>
              </Col>
              <Col xs={12} md={5} lg={3} className="h4 ">
                <ButtonWithMSVersions
                  action={updateMSToVersionX}
                  microService={microService}
                  actionName="Update to"
                />
              </Col>
            </>
          ) : (
            <>
              <Col xs={12} md={5} lg={3} className="h4 ">
                <Button
                  disabled={isLoading}
                  variant="warning w-100"
                  onClick={updateInstallerInfo}
                >
                  <span className="text-white">Retry</span>
                </Button>
              </Col>
              <Col xs={12} md={6} lg={3} className="h4">
                <ButtonWithMSVersions
                  microService={microService}
                  action={installMicroService}
                  actionName="Install"
                />
              </Col>
            </>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}

export default MicroserviceInstallation;
